import { getApiOrigin } from '@/config/env';
import { getToken } from '@/utils/auth';

export type DuitNowQrMode = 'default' | 'individual';

export type SiteSettings = {
  duitNowQrMode: DuitNowQrMode;
  hasDefaultDuitNowQr: boolean;
  paymentProofUrl: string;
  stockTrackerUrl: string;
};

function authHeaders(): HeadersInit {
  return {
    Authorization: `Bearer ${getToken()}`,
    'Content-Type': 'application/json',
  };
}

async function parseJsonError(res: Response, fallback: string): Promise<never> {
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  throw new Error(data.error || fallback);
}

function parseSettings(data: { settings?: SiteSettings }): SiteSettings | null {
  const settings = data.settings;
  if (
    !settings ||
    (settings.duitNowQrMode !== 'default' && settings.duitNowQrMode !== 'individual')
  ) {
    return null;
  }
  return {
    duitNowQrMode: settings.duitNowQrMode,
    hasDefaultDuitNowQr: Boolean(settings.hasDefaultDuitNowQr),
    paymentProofUrl: typeof settings.paymentProofUrl === 'string' ? settings.paymentProofUrl : '',
    stockTrackerUrl: typeof settings.stockTrackerUrl === 'string' ? settings.stockTrackerUrl : '',
  };
}

export async function fetchSiteSettings(): Promise<SiteSettings> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/site-settings`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    settings?: SiteSettings;
    error?: string;
  };

  const settings = parseSettings(data);
  if (!res.ok || !data.ok || !settings) {
    throw new Error(data.error || 'Failed to load site settings');
  }

  return settings;
}

export async function updateSiteSettings(input: {
  duitNowQrMode?: DuitNowQrMode;
  paymentProofUrl?: string;
  stockTrackerUrl?: string;
}): Promise<SiteSettings> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/site-settings`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    settings?: SiteSettings;
    error?: string;
  };

  const settings = parseSettings(data);
  if (!res.ok || !data.ok || !settings) {
    await parseJsonError(res, data.error || 'Failed to update site settings');
  }

  return settings!;
}

export async function fetchDefaultDuitNowQrBlob(): Promise<Blob> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/site-settings/default-duitnow-qr`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) {
    await parseJsonError(res, 'Failed to load DuitNow QR');
  }
  return res.blob();
}

export async function uploadDefaultDuitNowQr(file: File): Promise<SiteSettings> {
  const image = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('Failed to read image'));
    reader.readAsDataURL(file);
  });

  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/site-settings/default-duitnow-qr`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ image }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    settings?: SiteSettings;
    error?: string;
  };

  const settings = parseSettings(data);
  if (!res.ok || !data.ok || !settings) {
    throw new Error(data.error || 'Failed to upload DuitNow QR');
  }

  return settings;
}

export async function deleteDefaultDuitNowQr(): Promise<SiteSettings> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/site-settings/default-duitnow-qr`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    settings?: SiteSettings;
    error?: string;
  };

  const settings = parseSettings(data);
  if (!res.ok || !data.ok || !settings) {
    throw new Error(data.error || 'Failed to remove DuitNow QR');
  }

  return settings;
}
